// @greenlight-spec: 26-versioning-update-strategy.md#capacity-inputs-by-how-they-apply
// @greenlight-spec: 46-scaling.md
import { z } from 'zod';
/** Snapshot keys the dashboard may change. Everything else stays an installer flag. */
export const DASHBOARD_CAPACITY_KEYS = [
    'appNodeMinCount',
    'appNodeMaxCount',
    'systemNodeCount',
];
export const CapacityValuesSchema = z.object({
    appNodeMinCount: z.number().int(),
    appNodeMaxCount: z.number().int(),
    systemNodeCount: z.number().int(),
});
export const CAPACITY_APPLY_CLASSES = ['unattended', 'disrupts', 'manual'];
/**
 * How each capacity input in the install-input snapshot may change after
 * install. The table in docs/26 §Capacity inputs by how they apply is the
 * authority; this is its executable copy.
 */
export const CAPACITY_INPUT_CLASSES = {
    appNodeMinCount: {
        class: 'unattended',
        why: 'A bound on the app pool; the autoscaler moves the running count inside it.',
    },
    appNodeMaxCount: {
        class: 'unattended',
        why: 'Raising the ceiling stops nothing. A ceiling below the nodes running now is accepted only when their pods fit on the nodes that stay, with room left for one more pod of each app.',
    },
    systemNodeCount: {
        class: 'unattended',
        why: 'Adding a system node stops nothing. Removing one drains the node the update run may be running on, so it waits until the updater survives that drain.',
    },
    databaseSize: {
        class: 'disrupts',
        why: 'A tier change restarts the database server, and the control plane with it.',
    },
    appNodeSize: {
        class: 'manual',
        why: 'A new machine size replaces or recycles the pool, which no updater run may do.',
    },
    controlNodeSize: {
        class: 'manual',
        why: 'A new machine size recycles the system pool, including the node running the apply.',
    },
    sizingProfile: {
        class: 'manual',
        why: 'A profile only fills gaps at install; recorded sizes change field by field.',
    },
    nodeZone: {
        class: 'manual',
        why: 'Moving zones replaces every node pool.',
    },
};
/**
 * The dashboard's own ceiling on a node count. The templates accept up to
 * 1000; a mistyped digit on this control would be a large bill, so a count
 * past it is a break-glass installer change.
 */
export const DASHBOARD_NODE_COUNT_LIMIT = 100;
const LABELS = {
    appNodeMinCount: 'App pool minimum',
    appNodeMaxCount: 'App pool maximum',
    systemNodeCount: 'System node count',
};
/**
 * Validate a requested change against the effective values, with the same
 * rules the installer and the provider templates enforce, so a change the
 * apply would refuse is refused before it is recorded.
 */
export function checkCapacityChange(current, requested) {
    const changed = {};
    for (const key of DASHBOARD_CAPACITY_KEYS) {
        const value = requested[key];
        if (value === undefined)
            continue;
        if (!Number.isInteger(value) || value < 0 || value > DASHBOARD_NODE_COUNT_LIMIT) {
            return {
                ok: false,
                code: 'capacity.invalid_value',
                key,
                message: `${LABELS[key]} must be a whole number from 0 to ${DASHBOARD_NODE_COUNT_LIMIT}.`,
            };
        }
        if (value !== current[key])
            changed[key] = value;
    }
    if (Object.keys(changed).length === 0) {
        return {
            ok: false,
            code: 'capacity.nothing_to_change',
            message: 'The requested values are the ones the install already runs.',
        };
    }
    const next = { ...current, ...changed };
    if (next.appNodeMaxCount < 1) {
        return {
            ok: false,
            code: 'capacity.invalid_value',
            key: 'appNodeMaxCount',
            message: 'App pool maximum must be at least 1.',
        };
    }
    if (next.appNodeMinCount > next.appNodeMaxCount) {
        return {
            ok: false,
            code: 'capacity.invalid_value',
            key: 'appNodeMinCount',
            message: `App pool minimum (${next.appNodeMinCount}) cannot exceed its maximum (${next.appNodeMaxCount}).`,
        };
    }
    if (next.systemNodeCount < 1) {
        return {
            ok: false,
            code: 'capacity.invalid_value',
            key: 'systemNodeCount',
            message: 'System node count must be at least 1.',
        };
    }
    if (next.systemNodeCount < current.systemNodeCount) {
        return {
            ok: false,
            code: 'capacity.manual_change',
            key: 'systemNodeCount',
            message: 'Removing a system node drains the node the update run may be running on, and a run stopped mid-apply can leave the cluster changed without recording it. System nodes can be removed here once the updater survives that drain.',
        };
    }
    return { ok: true, changed, next };
}
function sumPods(pods) {
    return pods.reduce((acc, pod) => ({ cpu_m: acc.cpu_m + pod.cpu_m, memory_mib: acc.memory_mib + pod.memory_mib }), { cpu_m: 0, memory_mib: 0 });
}
function freeRoom(node) {
    const reserved = sumPods(node.pods);
    return {
        cpu_m: node.allocatable.cpu_m - reserved.cpu_m,
        memory_mib: node.allocatable.memory_mib - reserved.memory_mib,
        slots: node.allocatable.pods === null
            ? Number.POSITIVE_INFINITY
            : node.allocatable.pods - node.pods.length,
    };
}
/**
 * Whether lowering the app pool's maximum to `max` leaves room for what runs on
 * it. A maximum at or above the app nodes running now removes none. Below it,
 * the excess nodes are treated as removed, busiest first, because a cloud may
 * pick any node to remove (EKS's node group does); a node that cannot take pods
 * goes before any that can. Their pods are packed, largest first, into the free
 * CPU, memory and pod slots of each node that stays, never into the pool's
 * total. A rolling deploy starts one new pod of an app beside its old ones,
 * so one more of each app's largest pod must then still fit on some node that
 * stays (docs/26 §Capacity changes from the dashboard).
 */
export function checkAppPoolDecrease(nodes, max) {
    const pool = nodes.filter((node) => node.pool === 'apps');
    const excess = pool.length - max;
    if (excess <= 0)
        return { ok: true, removed: 0 };
    const movable = (node) => node.pods.filter((pod) => !pod.daemon);
    const load = (node) => {
        const reserved = sumPods(movable(node));
        return Math.max(node.allocatable.cpu_m > 0 ? reserved.cpu_m / node.allocatable.cpu_m : 1, node.allocatable.memory_mib > 0 ? reserved.memory_mib / node.allocatable.memory_mib : 1, node.allocatable.pods ? node.pods.length / node.allocatable.pods : 0);
    };
    const ordered = pool
        .map((node, index) => ({ node, index, load: load(node) }))
        .sort((a, b) => Number(a.node.room) - Number(b.node.room) || b.load - a.load || a.index - b.index);
    const removed = ordered.slice(0, excess).map((entry) => entry.node);
    const staying = ordered.slice(excess).map((entry) => entry.node);
    const targets = staying
        .filter((node) => node.room)
        .map((node) => ({ node, free: freeRoom(node) }));
    const largest = Math.max(1, ...pool.map((node) => node.allocatable.cpu_m));
    const largestMemory = Math.max(1, ...pool.map((node) => node.allocatable.memory_mib));
    const size = (pod) => Math.max(pod.cpu_m / largest, pod.memory_mib / largestMemory);
    const moving = removed.flatMap(movable).sort((a, b) => size(b) - size(a));
    const fits = (target, pod) => target.free.cpu_m >= pod.cpu_m &&
        target.free.memory_mib >= pod.memory_mib &&
        target.free.slots >= 1;
    const unplaced = [];
    for (const pod of moving) {
        let best = null;
        for (const target of targets) {
            if (!fits(target, pod))
                continue;
            const left = Math.min((target.free.cpu_m - pod.cpu_m) / largest, (target.free.memory_mib - pod.memory_mib) / largestMemory);
            const bestLeft = best
                ? Math.min((best.free.cpu_m - pod.cpu_m) / largest, (best.free.memory_mib - pod.memory_mib) / largestMemory)
                : Number.POSITIVE_INFINITY;
            if (left < bestLeft)
                best = target;
        }
        if (!best) {
            unplaced.push(pod);
            continue;
        }
        best.free.cpu_m -= pod.cpu_m;
        best.free.memory_mib -= pod.memory_mib;
        best.free.slots -= 1;
    }
    const refusal = {
        ok: false,
        code: 'capacity.does_not_fit',
        key: 'appNodeMaxCount',
        message: `Not enough room: your apps need more than ${max} ${max === 1 ? 'node' : 'nodes'}. Remove some apps first.`,
    };
    if (unplaced.length > 0)
        return refusal;
    const largestPod = new Map();
    for (const pod of pool.flatMap(movable)) {
        if (pod.app === null)
            continue;
        const seen = largestPod.get(pod.app);
        if (!seen || size(pod) > size(seen))
            largestPod.set(pod.app, pod);
    }
    const stalled = [...largestPod.values()].filter((pod) => !targets.some((target) => fits(target, pod)));
    return stalled.length === 0 ? { ok: true, removed: excess } : refusal;
}
/**
 * Parse the effective capacity values out of the install-input snapshot.
 * Null when any of the three is missing or not a count: an install whose
 * recording predates them has nothing the dashboard can safely change against.
 */
export function capacityValuesFromSnapshot(data) {
    const values = {};
    for (const key of DASHBOARD_CAPACITY_KEYS) {
        const raw = data[key];
        if (raw === undefined || !/^\d+$/.test(raw))
            return null;
        values[key] = Number(raw);
    }
    return values;
}
/** The installer flag each dashboard key travels to the updater's apply as. */
export const CAPACITY_INSTALLER_FLAGS = {
    appNodeMinCount: '--app-node-min-count',
    appNodeMaxCount: '--app-node-max-count',
    systemNodeCount: '--system-node-count',
};
/**
 * The ask at the app pool's ceiling: the pool runs at its maximum and app pods
 * still cannot be placed. The step grows the maximum by half (at least one
 * node), so a busy install is not asked again for every node it needs.
 */
export function ceilingAsk(input) {
    const { current } = input;
    if (input.pendingUnschedulable <= 0)
        return null;
    if (input.appNodeCount < current.appNodeMaxCount)
        return null;
    const step = Math.max(1, Math.ceil(current.appNodeMaxCount / 2));
    const proposed = Math.min(current.appNodeMaxCount + step, DASHBOARD_NODE_COUNT_LIMIT);
    if (proposed <= current.appNodeMaxCount)
        return null;
    return { key: 'appNodeMaxCount', current: current.appNodeMaxCount, proposed };
}
/** A resource is not suggested down within this long of being raised or asked up. */
export const CAPACITY_HYSTERESIS_DAYS = 30;
/** A declined ask stays quiet this long, unless the proposal grows. */
export const DECLINED_ASK_QUIET_DAYS = 7;
/** The next size down must hold the 30-day peak with this fraction to spare. */
export const SHRINK_HEADROOM = 0.3;
/** Distinct days of the 30-day window that must hold samples before the peak can be trusted. */
export const SHRINK_MIN_COVERAGE_DAYS = 28;
/**
 * The app pool's minimum is the only node count that bills while idle, so it
 * is the one worth suggesting down: when every 30-day peak fits one node fewer
 * with headroom. A suggestion never applies itself; accepting it is the same
 * request as any other change.
 */
export function shrinkSuggestion(input) {
    const { current, peak, perNodeAllocatable } = input;
    if (current.appNodeMinCount <= 0 || peak === null || perNodeAllocatable === null)
        return null;
    if (peak.coverage_days < SHRINK_MIN_COVERAGE_DAYS)
        return null;
    if (input.lastIncreaseAt !== null &&
        input.now.getTime() - input.lastIncreaseAt.getTime() < CAPACITY_HYSTERESIS_DAYS * 86_400_000) {
        return null;
    }
    const proposed = current.appNodeMinCount - 1;
    const usable = 1 - SHRINK_HEADROOM;
    const fits = peak.cpu_m <= proposed * perNodeAllocatable.cpu_m * usable &&
        peak.memory_mib <= proposed * perNodeAllocatable.memory_mib * usable;
    if (!fits)
        return null;
    return { key: 'appNodeMinCount', current: current.appNodeMinCount, proposed };
}
export const CAPACITY_CHANGE_STATUSES = [
    'pending',
    'applying',
    'applied',
    'failed',
    'superseded',
    'declined',
];
export const CAPACITY_CHANGE_SOURCES = ['manual', 'ask', 'suggestion'];
export const CAPACITY_CHANGE_SCHEDULES = ['window', 'now'];
const PartialCapacityValuesSchema = CapacityValuesSchema.partial();
export const CapacityChangeSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(CAPACITY_CHANGE_STATUSES),
    source: z.enum(CAPACITY_CHANGE_SOURCES),
    schedule: z.enum(CAPACITY_CHANGE_SCHEDULES),
    requested: PartialCapacityValuesSchema,
    previous: CapacityValuesSchema,
    requested_by: z.string().nullable(),
    run_id: z.string().uuid().nullable(),
    /** One bounded code, e.g. the updater's failure reason; never command output. */
    reason: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
});
export const CapacityInputClassSchema = z.object({
    key: z.string(),
    class: z.enum(CAPACITY_APPLY_CLASSES),
    why: z.string(),
    /** The recorded value, when the snapshot holds one. */
    value: z.string().nullable(),
});
export const CapacityPostureSchema = z.object({
    /** Null when the snapshot cannot be read or predates the three counts. */
    effective: CapacityValuesSchema.nullable(),
    inputs: z.array(CapacityInputClassSchema),
    /** The change waiting for, or riding, an updater run. */
    open_change: CapacityChangeSchema.nullable(),
    recent_changes: z.array(CapacityChangeSchema),
    ask: z
        .object({
        key: z.literal('appNodeMaxCount'),
        current: z.number().int(),
        proposed: z.number().int(),
    })
        .nullable(),
    suggestion: z
        .object({
        key: z.literal('appNodeMinCount'),
        current: z.number().int(),
        proposed: z.number().int(),
    })
        .nullable(),
    /**
     * False when the updater is off: a change could be recorded but nothing
     * would apply it. Null when the updater namespace could not be read, which
     * is not the same answer.
     */
    updater_enabled: z.boolean().nullable(),
    /** Nodes in the labeled app pool at the last health reading; null without one. */
    app_pool_node_count: z.number().int().nullable(),
});
export const RequestCapacityChangeSchema = z
    .object({
    values: PartialCapacityValuesSchema,
    schedule: z.enum(CAPACITY_CHANGE_SCHEDULES),
    source: z.enum(CAPACITY_CHANGE_SOURCES).default('manual'),
})
    .strict();
export const RequestCapacityChangeResponseSchema = z.object({
    change: CapacityChangeSchema,
    /** The triggered Job when schedule was `now` and the trigger accepted it. */
    job_name: z.string().nullable(),
    /** Why an immediate run did not start; the change still waits for the next one. */
    trigger_refusal: z.string().nullable(),
});
export const DeclineCapacityAskSchema = z
    .object({
    key: z.literal('appNodeMaxCount'),
    proposed: z.number().int().min(1).max(DASHBOARD_NODE_COUNT_LIMIT),
})
    .strict();
//# sourceMappingURL=capacity.js.map